import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Package2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useSound from 'use-sound';
import { db } from '../db';
import { supabase } from '../lib/supabase';
import type { Sku } from '../types';

const successSound = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';
const completeSound = 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3';

export default function Scanner() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [stats, setStats] = useState({ totalItems: 0, processedItems: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastScannedSku, setLastScannedSku] = useState<string | null>(null);
  const [showDifferentSkuAlert, setShowDifferentSkuAlert] = useState<string | null>(null);
  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playComplete] = useSound(completeSound, { volume: 0.5 });

  const loadStats = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: skus, error } = await supabase
        .from('skus')
        .select('expected_quantity, actual_quantity')
        .or(`user_id.eq.${session.user.id},user_id.is.null`);

      if (error) throw new Error(`SKU 조회 실패: ${error.message}`);

      const total = skus.reduce((sum, sku) => sum + (sku.expected_quantity || 0), 0);
      const processed = skus.reduce((sum, sku) => sum + (sku.actual_quantity || 0), 0);

      setStats({ totalItems: total, processedItems: processed });
    } catch (error: any) {
      console.error('통계 로드 실패:', error.message);
      toast.error(`통계 로드 실패: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    loadStats();
    inputRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      if (activeElement?.tagName !== 'INPUT' || activeElement === inputRef.current) {
        inputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loadStats]);

  const handleBarcode = async (code: string) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const parts = code.split('/');
      if (parts.length !== 3) {
        toast.error('잘못된 바코드 형식입니다 (예: MMDJK5/BE/M)');
        return;
      }

      const [styleNo, color, size] = parts;
      const skuId = `${styleNo}-${color}-${size}`;

      if (lastScannedSku && lastScannedSku !== skuId) {
        setShowDifferentSkuAlert(skuId);
        return;
      }

      const { data: sku, error } = await supabase
        .from('skus')
        .select('*')
        .eq('id', skuId)
        .single();

      if (error || !sku) {
        throw new Error(`입고 예정 SKU를 찾을 수 없습니다: ${skuId}`);
      }

      const newQuantity = (sku.actual_quantity || 0) + 1;
      const progress = Math.min((newQuantity / sku.expected_quantity) * 100, 100);
      const now = new Date().toISOString();

      const { error: updateError } = await supabase
        .from('skus')
        .update({
          actual_quantity: newQuantity,
          progress,
          status: progress >= 100 ? 'completed' : 'pending',
          last_scanned: now,
          last_modified: now,
        })
        .eq('id', skuId);

      if (updateError) throw new Error(`SKU 업데이트 실패: ${updateError.message}`);

      await db.transaction('rw', db.skus, db.logs, async () => {
        await db.skus.update(skuId, {
          actualQuantity: newQuantity,
          progress,
          status: progress >= 100 ? 'completed' : 'pending',
          lastScanned: new Date(now),
          lastModified: new Date(now),
        });

        await db.logs.add({
          id: crypto.randomUUID(),
          timestamp: new Date(),
          type: 'scan',
          message: `스캔된 SKU: ${styleNo}`,
          skuId,
        });
      });

      setStats((prev) => ({
        ...prev,
        processedItems: prev.processedItems + 1,
      }));

      setLastScannedSku(skuId);

      playSuccess();
      if (newQuantity === sku.expected_quantity) {
        playComplete();
        toast.success(`${skuId} 입고 완료!`, { duration: 3000, icon: '🎉' });
      } else {
        toast.success(`스캔 완료: ${skuId} (${newQuantity}/${sku.expected_quantity})`);
      }

      await loadStats();
    } catch (error: any) {
      console.error('바코드 처리 중 오류:', error.message);
      toast.error(`바코드 처리 실패: ${error.message}`);
    } finally {
      setIsProcessing(false);
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    }
  };

  const handleDifferentSkuConfirm = async (confirm: boolean) => {
    if (!showDifferentSkuAlert) return;

    if (confirm) {
      setLastScannedSku(showDifferentSkuAlert);
      await handleBarcode(showDifferentSkuAlert.replace(/-/g, '/'));
    } else {
      setLastScannedSku(null);
    }
    setShowDifferentSkuAlert(null);
  };

  const progress = stats.totalItems > 0 ? (stats.processedItems / stats.totalItems) * 100 : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
      <div className="space-y-4 md:space-y-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">전체 진행률</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs md:text-sm text-gray-600">
              <span>처리완료: {stats.processedItems.toLocaleString()}개</span>
              <span>{progress.toFixed(1)}%</span>
              <span>전체물량: {stats.totalItems.toLocaleString()}개</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">바코드 스캔</h3>
          <div className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 border-2 border-dashed rounded-lg bg-gray-50">
            <Package2 className="w-5 h-5 md:w-6 md:h-6 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="바코드를 스캔하거나 입력하세요 (예: MMDJK5/BE/M)"
              className="flex-1 bg-white border rounded-lg px-3 md:px-4 py-2 text-sm md:text-base"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isProcessing) {
                  handleBarcode(e.currentTarget.value);
                }
              }}
              disabled={isProcessing || !!showDifferentSkuAlert}
              autoComplete="off"
            />
            {isProcessing && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            )}
          </div>
        </div>

        {showDifferentSkuAlert && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                다른 상품 입고처리 진행 하겠습니까?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                이전 SKU: {lastScannedSku} → 현재 SKU: {showDifferentSkuAlert}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => handleDifferentSkuConfirm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  아니오
                </button>
                <button
                  onClick={() => handleDifferentSkuConfirm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  예
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}